import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { FC, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { previewPipelineCampaign, sendPipelineCampaign } from '../api/referrals';
import { LeadNewsletterComposer } from './leads/LeadNewsletterComposer';
import { useAuthState } from '../contexts/authcontext';
import { LeadCampaignAudiencePreview } from '../data/types';

type FundingPipelineCampaignProps = {
  resourceId?: string
  partnerSlug?: string
};

/** Open Create campaign with the Joint seminar invite for this resource's pipeline. */
export const FundingPipelineCampaign: FC<FundingPipelineCampaignProps> = ({
  resourceId,
  partnerSlug,
}) => {
  const { communityId, authUser } = useAuthState();
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<LeadCampaignAudiencePreview | null>(null);
  const adminEmail = authUser?.email || authUser?.username || '';

  useEffect(() => {
    if (!isOpen || !communityId || !resourceId) return;
    let cancelled = false;
    previewPipelineCampaign(communityId, resourceId, partnerSlug)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        if (!cancelled) setPreview({ total: 0, audience_label: 'Partner referrals with emails' });
      });
    return () => {
      cancelled = true;
    };
  }, [communityId, isOpen, partnerSlug, resourceId]);

  if (!resourceId) return null;

  return (
    <>
      <button
        type="button"
        data-testid="pipeline-create-campaign"
        className="inline-flex items-center gap-2 rounded-lg bg-[#5DB6CE] px-4 py-2 text-sm font-semibold text-white/90 font-raleway"
        onClick={() => setIsOpen(true)}
      >
        <MailOutlineIcon sx={{ fontSize: 18 }} />
        Create campaign
      </button>
      <LeadNewsletterComposer
        isOpen={isOpen}
        adminEmail={adminEmail}
        recipientSummary={preview?.audience_label || 'Partner referrals with emails on this funding resource'}
        audience={{ type: 'funding_pipeline', resolved_count: preview?.total }}
        audiencePreview={preview}
        initialTemplateId="joint-seminar-invite"
        persistToLeads={false}
        onClose={() => setIsOpen(false)}
        onSaved={() => undefined}
        onRequestSend={async (draft) => {
          if (!communityId || !resourceId) return;
          try {
            const result = await sendPipelineCampaign(communityId, resourceId, {
              name: draft.name,
              subject: draft.subject,
              message_html: draft.messageHtml,
              partner_slug: partnerSlug,
            });
            toast.success(`Campaign sent to ${result?.delivery?.requested ?? 0} pipeline members`);
            setIsOpen(false);
          } catch {
            toast.error('Could not send the pipeline campaign');
          }
        }}
      />
    </>
  );
};
